import React, {useCallback, useMemo} from "react";
import {
    getCampaignPlayerView,
    updateCampaignPlayerView,
} from "@/api/PlayerViewApi.ts";
import {useRevisionedRealtimeState} from "@/api/realtime/useRevisionedRealtimeState.ts";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import Encounter from "@/encounter/model/Encounter.ts";
import {
    PlayerDisplay,
    PlayerViewContext,
    PlayerViewReturn,
    PlayerViewSelection,
    PlayerViewSelectionUpdate,
} from "@/player/PlayerViewHooks.ts";
import {Scene} from "@/scenes/Scene.ts";

const CAMPAIGN: PlayerViewSelection = {type: "NONE", revision: -1};

export default function PlayerViewProvider({children}: { children: React.ReactNode }) {
    const {campaign} = useCampaign();
    const load = useCallback(() => getCampaignPlayerView(campaign.id), [campaign.id]);
    const persist = useCallback(
        (change: PlayerViewSelectionUpdate) => updateCampaignPlayerView(campaign.id, change),
        [campaign.id],
    );
    const {value: selection, loading, updating, error, update} = useRevisionedRealtimeState({
        topic: `/campaigns/${campaign.id}/player-view`,
        initialValue: CAMPAIGN,
        load,
        update: persist,
    });

    const display = useMemo(() => toDisplay(selection), [selection]);
    const displayEncounter = useCallback(async (target: Encounter) => {
        return await update({type: "ENCOUNTER", encounterId: target.id});
    }, [update]);
    const displayScene = useCallback(async (scene: Scene) => {
        await update({type: "SCENE", sceneId: scene.id});
    }, [update]);
    const displayCampaign = useCallback(async () => {
        await update({type: "NONE"});
    }, [update]);

    const value = useMemo<PlayerViewReturn>(() => ({
        display,
        displayEncounter,
        displayScene,
        displayCampaign,
        loading: loading || updating,
        error,
    }), [display, displayCampaign, displayEncounter, displayScene, error, loading, updating]);

    return <PlayerViewContext.Provider value={value}>{children}</PlayerViewContext.Provider>;
}

function toDisplay(selection: PlayerViewSelection): PlayerDisplay {
    switch (selection.type) {
        case "NONE":
            return {display: "campaign"};
        case "ENCOUNTER":
            return {
                display: "encounter",
                encounter: {id: selection.encounterId, name: selection.encounterName},
            };
        case "SCENE":
            return {
                display: "scene",
                scene: {id: selection.sceneId, name: selection.sceneName},
            };
    }
}
